import React, { useState, useEffect } from "react";
import { Menu, MenuItem, Button, Box } from "@mui/material";

const ProviderDropdown = ({ rawData, selectedProvider, setSelectedProvider, placeholder }) => {
  const data = {
    Provider: rawData.objProviderGroups,
    Nurse: rawData.objNurseGroups,
    Counselor: rawData.objCounselorGroups,
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);
  const [providerMenuAnchorEl, setProviderMenuAnchorEl] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedCertification, setSelectedCertification] = useState("");
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    // Pre-fill provider if in update mode
    if (selectedProvider) {
      const prefilledProvider = Object.values(data)?.flatMap((group) => group?.flatMap((cert) => cert.objProviderList))
        .find((prov) => prov?.ProviderID === selectedProvider);

      if (prefilledProvider) {
        setProvider(prefilledProvider);
        setSelectedRole(prefilledProvider.RoleID); // Ensure role is pre-selected
      }
    }
  }, [selectedProvider, data]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSubMenuAnchorEl(null);
    setProviderMenuAnchorEl(null);
  };

  const handleSubMenuOpen = (event, role) => {
    setSelectedRole(role);
    setSubMenuAnchorEl(event.currentTarget);
    setProviderMenuAnchorEl(null);
    setSelectedCertification(""); // Reset certification
    setProviders([]); // Clear providers
    setProvider(null); // Clear provider
    setSelectedProvider("")
  };

  const handleProviderMenuOpen = (event, certificationID) => {
    setSelectedCertification(certificationID);

    // Fetch the updated provider list for the selected certification
    const roleData = data[selectedRole]?.find(
      (cert) => cert.CertificaionID === certificationID
    );

    setProviders(roleData?.objProviderList || []);
    setProviderMenuAnchorEl(event.currentTarget);
    setProvider(null); // Clear provider on certification change
  };

  const handleProviderSelect = (selected) => {
    setSelectedProvider(selected.ProviderID); // Save selected provider's ID
    setProvider(selected); // Save selected provider object
    handleMenuClose(); // Close all menus
  };

  const getButtonLabel = () => {
    if (provider) {
      return `${provider.FirstName} ${provider.LastName}`;
    }
    return placeholder || "Select Role";
  };

  return (
    <Box>
      <Button
        variant="contained"
        onClick={handleMenuOpen}
        className={`ib2-cascade-trigger ${provider ? "ib2-cascade-filled" : "ib2-cascade-empty"}`}
      >
        {getButtonLabel()}
      </Button>
      {/* Role Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{ dense: true }}
      >
        {Object.keys(data)?.map((role) => (
          <MenuItem
            key={role}
            onMouseEnter={(event) => handleSubMenuOpen(event, role)}
            sx={{
              '&:hover': {
                backgroundColor: 'blue',
                color: 'white',
              },
              backgroundColor: selectedRole === role ? 'blue' : 'transparent',
              color: selectedRole === role ? 'white' : 'inherit',
            }}
          >
            {role}
          </MenuItem>
        ))}
      </Menu>

      {/* Certification Menu */}
      <Menu
        anchorEl={subMenuAnchorEl}
        open={Boolean(subMenuAnchorEl)}
        onClose={() => setSubMenuAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
      >
        {selectedRole &&
          data[selectedRole]?.map((certification) => (
            <MenuItem
              key={certification.CertificaionID}
              onMouseEnter={(event) =>
                handleProviderMenuOpen(event, certification.CertificaionID)
              }
              sx={{
                '&:hover': {
                  backgroundColor: 'blue',
                  color: 'white',
                },
                backgroundColor: selectedCertification === certification.CertificaionID ? 'blue' : 'transparent',
                color: selectedCertification === certification.CertificaionID ? 'white' : 'inherit',
              }}
            >
              {certification.CertificaionName}
            </MenuItem>
          ))}
      </Menu>

      {/* Provider Menu */}
      <Menu
        anchorEl={providerMenuAnchorEl}
        open={Boolean(providerMenuAnchorEl)}
        onClose={() => setProviderMenuAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
      >
        {providers?.map((prov) => (
          <MenuItem
            key={prov.ProviderID}
            onClick={() => handleProviderSelect(prov)}
            sx={{
              '&:hover': {
                backgroundColor: 'blue',
                color: 'white',
              },
              backgroundColor: provider?.ProviderID === prov.ProviderID ? 'blue' : 'transparent',
              color: provider?.ProviderID === prov.ProviderID ? 'white' : 'inherit',
            }}
          >
            {prov.FirstName} {prov.LastName}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ProviderDropdown;
